/**
 * C05 Day-1 Hook — Tutorial Boss ("練習魔王・木靶王").
 *
 * Built entirely on the G02 FSM scaffolding (same pattern as demo-boss.ts).
 * Independent BossDef + BossFSMConfig + opt-in pool (`BOSS_TUTORIAL_POOL`) so
 * it NEVER leaks into the legacy 9-boss balance (`BOSS_POOL`, tier-2 pool,
 * tier-3 pool). This is the C05 red-line: "不動既有 boss(只加 tutorial-boss)".
 *
 * Design intent — MUST-WIN (per spec):
 *   - HP 80     — equivalent to 1-2 successful spread-shot volleys for a
 *                 fresh kid; bosses normally start at 400-2000 HP.
 *   - speed     — descends slowly (20 px/s vs typical 40-50)
 *   - damage    — single-shot 5 dmg every 4s with telegraph; max DPS is ~1.25
 *                 vs the kid's typical 50 HP pool → kid survives ≥40s even
 *                 standing still
 *   - phase 1 only — no berserk-mode flip; no phase-gate; no attack-B
 *                 (attackB undefined so the FSM channel stays silent)
 *   - no minions, no silences, no freezes — purely a dummy target so the
 *                 kid feels "我打贏了魔王!" within 30s
 *
 * Reward (granted by scene wire-up calling `completeTutorialBoss`):
 *   - 200 freeXP (TUTORIAL_BOSS_XP in dayone-hook.ts)
 *
 * Not wired into combat scenes yet — that is a follow-up task tracked under
 * C05. The def is exported as `BOSS_TUTORIAL_POOL` so the future scene
 * integration can simply `import { BOSS_TUTORIAL_POOL } from .../tutorial-boss`
 * and spawn the first entry when `ef:start-tutorial-boss` fires.
 */

import type { BossDef, BossAttack, Boss } from '../../entities/boss';
import { ASSETS, GAME } from '../../config';
import { wrapBossDefWithFSM, type BossFSMConfig } from '../boss-fsm';

/** Stable id used by completeTutorialBoss / bossesDefeated tracking. */
export const TUTORIAL_BOSS_ID = 'tutorial-boss';

/**
 * FSM config — phase-1 only, single-shot at long cooldown.
 * Numbers tuned so the kid wins in ~15-30s regardless of skill loadout.
 */
export const TUTORIAL_BOSS_FSM_CONFIG: BossFSMConfig = {
  attackA: {
    1: (self: Boss, _nowMs: number, playerX: number): BossAttack[] => {
      const angle = Math.atan2(playerX - self.x, GAME.height * 0.85 - self.y);
      // Damage = 5; spreadshot starts kids at ~50 HP so 10 hits to die →
      // bounded by 4s cooldown that means ≥40s of standing-still survival.
      return [{ kind: 'single-shot', angleRad: angle, speedPxPerSec: 220, damage: 5 }];
    },
  },
  // attackB intentionally omitted — no second channel, no phase-gate.
  attackBPhaseGate: 99, // belt-and-braces: even if attackB were ever added, gate it out forever
};

/**
 * BossDef template — assetKey reuses an existing sprite so we don't ship a
 * new SVG (the def is a stripped-down trainer dummy; reskin later if PL wants).
 */
const TUTORIAL_BOSS_DEF_TEMPLATE: BossDef = {
  id: TUTORIAL_BOSS_ID,
  displayName: '練習魔王・木靶王',
  hp: 80,                 // ≤ 2 spread-shot volleys for a fresh kid
  speedPxPerSec: 20,      // slow descent
  size: 96,
  color: 0x8d6e63,        // muted brown — clearly distinct from real bosses
  assetKey: ASSETS.bossShuMo,
  shape: 'diamond',
  faction: 'celestial',
  affinityFaction: 'celestial',
  tier: 1,
  attackACooldownMs: 4000, // 1 shot every 4s
  attackBCooldownMs: 999999, // effectively never (attackB omitted anyway)
  phase2HpThreshold: 0,    // never enters phase 2
  attackBWarningMs: 1200,   // longer telegraph for kids to learn dodging
  introDialog: '哈囉指揮官!來打靶練習一下吧!',
  entranceCinematic: 'descend',
  deathCinematic: 'fragment-into-pieces',
  // tickAttacks replaced by wrapBossDefWithFSM
  tickAttacks: () => [],
};

/** Production-ready FSM-wrapped tutorial boss def. */
export const TUTORIAL_BOSS: BossDef = wrapBossDefWithFSM(
  TUTORIAL_BOSS_DEF_TEMPLATE,
  TUTORIAL_BOSS_FSM_CONFIG,
);

/**
 * Opt-in pool — NOT wired into the legacy BOSS_POOL / tier pools.
 * Scene integration will import this directly when handling the
 * `ef:start-tutorial-boss` window event (see dayone-banner.ts).
 */
export const BOSS_TUTORIAL_POOL: BossDef[] = [TUTORIAL_BOSS];
